import React, { useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { useGenogramAssistantUIStore } from '../../stores/genogramAssistantUIStore';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { ScrollArea } from '../ui/scroll-area';
import { useTranslation } from '@/hooks/useTranslation';
import {
  type InsightPanelTranslateFn,
  localizeInsightApplySummary,
  localizeInsightArtifactLabel,
  localizeInsightArtifactValue,
  localizeInsightDescription,
  localizeInsightFollowUp,
  localizeInsightRefineHint,
  localizeInsightSectionItem,
  localizeInsightSectionTitle,
  localizeInsightStatusNote,
  localizeInsightTitle,
} from '../../domains/genogram/utils/insightDeterministic';

export const GenogramInsightPanel: React.FC = () => {
  const { t } = useTranslation('genogram');
  const insightT = t as InsightPanelTranslateFn;
  const tabs = useMemo(
    () =>
      [
        { id: 'proposed_changes' as const, label: t('insights.tabs.proposedChanges') },
        { id: 'analysis' as const, label: t('insights.tabs.analysis') },
        { id: 'evidence' as const, label: t('insights.tabs.evidence') },
        { id: 'report_preview' as const, label: t('insights.tabs.reportPreview') },
      ] as const,
    [t],
  );
  const tab = useGenogramAssistantUIStore((s) => s.activeInsightTab);
  const payloadByTab = useGenogramAssistantUIStore((s) => s.payloadByTab);
  const loadingByTab = useGenogramAssistantUIStore((s) => s.loadingByTab);
  const errorByTab = useGenogramAssistantUIStore((s) => s.errorByTab);
  const actionsByTab = useGenogramAssistantUIStore((s) => s.actionsByTab);
  const setTab = useGenogramAssistantUIStore((s) => s.setInsightTab);
  const payload = payloadByTab[tab] ?? null;

  const visibleTabs = useMemo(
    () =>
      tabs.filter(
        (item) => payloadByTab[item.id] || loadingByTab[item.id] || errorByTab[item.id],
      ),
    [tabs, payloadByTab, loadingByTab, errorByTab],
  );

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((item) => item.id === tab)) {
      setTab(visibleTabs[0].id);
    }
  }, [visibleTabs, tab, setTab]);

  useEffect(() => {
    if (payload) {
      SessionMetrics.increment('genogram.ai.insight_panel_open');
      SessionMetrics.increment('genogram.ai_v2.insight_panel.opened');
      Tracker.track('genogram.ai_v2.insight_panel.opened', { tab });
    }
  }, [payload, tab]);

  const renderTabSummary = (tabId: (typeof tabs)[number]['id']) => {
    const tabPayload = payloadByTab[tabId] ?? null;
    const isLoading = Boolean(loadingByTab[tabId]);
    const error = errorByTab[tabId];
    const actions = actionsByTab[tabId] ?? null;
    const showActionButtons = Boolean(
      actions &&
        (tabId === 'proposed_changes' ||
          tabPayload?.toolName === 'genogram_fieldpack_enrich' ||
          (tabPayload?.metrics?.updates ?? 0) > 0),
    );

    if (isLoading) {
      return <div className="text-xs text-muted-foreground">{t('insights.generating')}</div>;
    }

    if (error) {
      return <div className="text-xs text-destructive">{error}</div>;
    }

    if (!tabPayload) {
      return <div className="text-xs text-muted-foreground">{t('insights.emptyPanel')}</div>;
    }

    return (
      <div className="space-y-3">
        <div>
          <div className="font-medium">{localizeInsightTitle(tabPayload.title, insightT)}</div>
          {tabPayload.description ? (
            <div className="mt-1 text-muted-foreground">{localizeInsightDescription(tabPayload.description, insightT)}</div>
          ) : null}
          {tabPayload.statusNote ? (
            <div className="mt-2 text-xs text-muted-foreground">
              {localizeInsightStatusNote(tabPayload.statusNote, insightT)}
            </div>
          ) : null}
          {tabPayload.applySummary ? (
            <div className="mt-1 text-xs text-muted-foreground">
              {localizeInsightApplySummary(tabPayload.applySummary, insightT)}
            </div>
          ) : null}
          {tabPayload.refineHint ? (
            <div className="mt-1 text-xs text-muted-foreground">{localizeInsightRefineHint(tabPayload.refineHint, insightT)}</div>
          ) : null}
        </div>

        {tabPayload.metrics ? (
          <div className="grid grid-cols-2 gap-2 rounded border bg-muted/30 p-3 text-xs">
            <div>{t('insights.metrics.additions', { count: tabPayload.metrics.additions ?? 0 })}</div>
            <div>{t('insights.metrics.removals', { count: tabPayload.metrics.removals ?? 0 })}</div>
            <div>{t('insights.metrics.moves', { count: tabPayload.metrics.moves ?? 0 })}</div>
            <div>{t('insights.metrics.updates', { count: tabPayload.metrics.updates ?? 0 })}</div>
          </div>
        ) : null}

        {tabPayload.artifacts && tabPayload.artifacts.length > 0 ? (
          <div className="rounded border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('insights.outputs')}
            </div>
            <div className="space-y-1.5 text-xs">
              {tabPayload.artifacts.map((artifact) => (
                <div key={`${artifact.label}-${artifact.value}`} className="flex items-start justify-between gap-3">
                  <span className="text-muted-foreground">{localizeInsightArtifactLabel(artifact.label, insightT)}</span>
                  <span className="text-right font-medium">{localizeInsightArtifactValue(artifact.value, insightT)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {tabPayload.sections && tabPayload.sections.length > 0 ? (
          <div className="space-y-2">
            {tabPayload.sections.map((section) => (
              <div key={section.title} className="rounded border bg-background p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {localizeInsightSectionTitle(section.title, insightT)}
                </div>
                <div className="space-y-1.5 text-sm">
                  {section.items.map((item) => (
                    <div key={`${section.title}-${item}`} className="flex gap-2">
                      <span className="text-muted-foreground">•</span>
                      <span>{localizeInsightSectionItem(item, t)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {tabPayload.suggestedFollowUps && tabPayload.suggestedFollowUps.length > 0 ? (
          <div className="rounded border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('insights.suggestedFollowUps')}
            </div>
            <div className="space-y-1.5 text-sm">
              {tabPayload.suggestedFollowUps.map((item) => (
                <div key={item} className="flex gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>{localizeInsightFollowUp(item, insightT)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {tabPayload.conflicts && tabPayload.conflicts.length > 0 ? (
          <div className="rounded border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('insights.conflicts')}
            </div>
            <div className="space-y-1.5 text-sm">
              {tabPayload.conflicts.map((item) => (
                <div key={`${item.targetId}-${item.fieldPath}`} className="flex gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>{item.targetId} {item.fieldPath} ({item.classification})</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {tabPayload.uncertainClaims && tabPayload.uncertainClaims.length > 0 ? (
          <div className="rounded border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('insights.uncertainClaims')}
            </div>
            <div className="space-y-1.5 text-sm">
              {tabPayload.uncertainClaims.map((item) => (
                <div key={item.claimId} className="flex gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>{item.reason}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {showActionButtons ? (
          <div className="flex flex-wrap gap-2">
            {actions?.onApply ? (
              <button
                type="button"
                data-testid="tid.genogram.insight-panel.action.apply"
                className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                onClick={() => { void actions.onApply?.(); }}
              >
                {t('insights.applyChanges')}
              </button>
            ) : null}
            {actions?.onDiscard ? (
              <button
                type="button"
                data-testid="tid.genogram.insight-panel.action.discard"
                className="rounded border px-3 py-1.5 text-xs font-medium"
                onClick={() => { void actions.onDiscard?.(); }}
              >
                {t('insights.discard')}
              </button>
            ) : null}
            {tabId === 'proposed_changes' && actions?.onRefine ? (
              <button
                type="button"
                data-testid="tid.genogram.insight-panel.action.refine"
                className="rounded border px-3 py-1.5 text-xs font-medium"
                onClick={() => { void actions.onRefine?.(); }}
              >
                {t('insights.refine')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col" data-testid="tid.genogram.insight-panel">
      <div className="flex h-full flex-col" data-testid="tid.genogram.insight.panel">
      {visibleTabs.length > 1 && (
        <div className="flex border-b px-2 py-2" role="tablist" aria-label={t('insights.insightTabsAria')}>
          {visibleTabs.map((item) => (
            <button
              key={item.id}
              type="button"
              id={`genogram-insight-tab-${item.id}`}
              role="tab"
              aria-selected={tab === item.id}
              data-testid={`tid.genogram.insight-panel.tab.${item.id}`}
              onClick={() => setTab(item.id)}
              className={cn(
                'rounded px-2 py-1 text-xs font-medium transition-colors',
                tab === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1">
        {visibleTabs.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground">{t('insights.emptyPanel')}</div>
        ) : (
          visibleTabs.map((item) => (
            <div
              key={item.id}
              role="tabpanel"
              aria-labelledby={`genogram-insight-tab-${item.id}`}
              className={cn('h-full min-h-0', tab === item.id ? 'block' : 'hidden')}
            >
              <ScrollArea className="h-full">
                <div className="p-4 text-sm">
                  {renderTabSummary(item.id)}
                </div>
              </ScrollArea>
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  );
};
